/**
 * Hook pour gérer les tests de récompenses
 * @module admin/hooks/useRewardsTest
 */

import { useState, useEffect, useCallback } from 'react';
import { getRewardTypes, simulateReward, getRewards } from '../services/rewardsTestService';

const DEFAULT_TEST_OPTIONS = {
  userInput: '',
  username: 'test_user',
  displayName: 'Test User',
  userId: '',
  rewardId: '',
};

const HISTORY_STORAGE_KEY = 'rewardsTestHistory';
const MAX_HISTORY_ENTRIES = 50;

/**
 * Load history from localStorage
 * @returns {Array} Parsed history or empty array
 */
const loadHistoryFromStorage = () => {
  try {
    const savedHistory = localStorage.getItem(HISTORY_STORAGE_KEY);
    return savedHistory ? JSON.parse(savedHistory) : [];
  } catch (err) {
    console.error('Erreur lors du chargement de l\'historique:', err);
    return [];
  }
};

/**
 * Save history to localStorage
 * @param {Array} history - History entries to save
 */
const saveHistoryToStorage = (history) => {
  localStorage.setItem(
    HISTORY_STORAGE_KEY,
    JSON.stringify(history.slice(0, MAX_HISTORY_ENTRIES))
  );
};

/**
 * Hook de gestion des tests de récompenses
 * @returns {Object} État et fonctions pour les tests de récompenses
 */
export const useRewardsTest = () => {
  const [rewardTypes, setRewardTypes] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [selectedRewardTitle, setSelectedRewardTitle] = useState('');
  const [selectedRewardId, setSelectedRewardId] = useState('');
  const [testOptions, setTestOptions] = useState(DEFAULT_TEST_OPTIONS);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingRewards, setIsLoadingRewards] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);

  /**
   * Récupère la liste des types de récompenses
   */
  const fetchRewardTypes = useCallback(async () => {
    try {
      const data = await getRewardTypes();
      if (data.success) {
        setRewardTypes(data.types);
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des types de récompenses:', err);
      setError('Erreur lors de la récupération des types de récompenses');
    }
  }, []);

  /**
   * Récupère la liste des récompenses Twitch
   */
  const fetchRewards = useCallback(async (onlyManageable = false) => {
    setIsLoadingRewards(true);
    setError(null);
    try {
      const data = await getRewards(onlyManageable);
      if (data.success) {
        setRewards(data.rewards || []);
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des récompenses:', err);
      setError('Erreur lors de la récupération des récompenses');
    } finally {
      setIsLoadingRewards(false);
    }
  }, []);

  /**
   * Gère le changement de titre de récompense
   * @param {string} rewardTitle - Titre de la récompense
   */
  const handleRewardTitleChange = useCallback((rewardTitle) => {
    setSelectedRewardTitle(rewardTitle);
    // Si le titre ne correspond à aucune récompense de la liste, réinitialiser l'ID
    const matchingReward = rewards.find((r) => r.title === rewardTitle);
    if (!matchingReward) {
      setSelectedRewardId('');
      setTestOptions((prev) => ({
        ...prev,
        rewardId: '',
      }));
    }
    setResult(null);
    setError(null);
  }, [rewards]);

  /**
   * Gère la sélection d'une récompense depuis la liste
   * @param {string} rewardId - ID de la récompense (vide pour désélectionner)
   */
  const handleRewardSelect = useCallback((rewardId) => {
    if (!rewardId) {
      setSelectedRewardId('');
      setSelectedRewardTitle('');
      setTestOptions((prev) => ({
        ...prev,
        rewardId: '',
      }));
      return;
    }

    const reward = rewards.find((r) => r.id === rewardId);
    if (reward) {
      setSelectedRewardTitle(reward.title);
      setSelectedRewardId(reward.id);
      setTestOptions((prev) => ({
        ...prev,
        rewardId: reward.id,
      }));
      setResult(null);
      setError(null);
    }
  }, [rewards]);

  /**
   * Gère le changement d'une option de test
   * @param {string} optionName - Nom de l'option
   * @param {*} value - Nouvelle valeur
   */
  const handleOptionChange = useCallback((optionName, value) => {
    setTestOptions((prev) => ({
      ...prev,
      [optionName]: value,
    }));
  }, []);

  /**
   * Ajoute une entrée à l'historique
   * @param {Object} entry - Entrée à ajouter
   */
  const addToHistory = useCallback((rewardTitle, options, data) => {
    const historyEntry = {
      id: Date.now(),
      rewardTitle,
      options: { ...options },
      result: data,
      timestamp: new Date().toISOString(),
    };
    setHistory((prev) => [historyEntry, ...prev].slice(0, MAX_HISTORY_ENTRIES));
  }, []);

  /**
   * Simule une rédemption de récompense
   */
  const handleSimulateReward = useCallback(async () => {
    if (!selectedRewardTitle) {
      setError('Veuillez saisir ou sélectionner un titre de récompense');
      return;
    }

    setIsLoading(true);
    setResult(null);
    setError(null);

    try {
      const data = await simulateReward(selectedRewardTitle, testOptions);
      setResult(data);
      addToHistory(selectedRewardTitle, testOptions, data);
    } catch (err) {
      setError(err.message || 'Erreur lors de la simulation de la récompense');
    } finally {
      setIsLoading(false);
    }
  }, [selectedRewardTitle, testOptions, addToHistory]);

  /**
   * Rejoue un test depuis l'historique
   * @param {Object} entry - Entrée de l'historique
   */
  const replayTest = useCallback((entry) => {
    setSelectedRewardTitle(entry.rewardTitle);
    setTestOptions(entry.options);
  }, []);

  /**
   * Supprime une entrée de l'historique
   * @param {number} id - ID de l'entrée
   */
  const deleteHistoryEntry = useCallback((id) => {
    setHistory((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  /**
   * Efface tout l'historique
   */
  const clearHistory = useCallback(() => {
    setHistory([]);
    localStorage.removeItem(HISTORY_STORAGE_KEY);
  }, []);

  // Charger les types de récompenses au montage
  useEffect(() => {
    fetchRewardTypes();
    fetchRewards();
  }, [fetchRewardTypes, fetchRewards]);

  // Charger l'historique depuis localStorage
  useEffect(() => {
    setHistory(loadHistoryFromStorage());
  }, []);

  // Sauvegarder l'historique dans localStorage
  useEffect(() => {
    if (history.length > 0) {
      saveHistoryToStorage(history);
    }
  }, [history]);

  return {
    rewardTypes,
    rewards,
    selectedRewardTitle,
    selectedRewardId,
    testOptions,
    isLoading,
    isLoadingRewards,
    result,
    error,
    history,
    fetchRewardTypes,
    fetchRewards,
    handleRewardTitleChange,
    handleRewardSelect,
    handleOptionChange,
    handleSimulateReward,
    replayTest,
    deleteHistoryEntry,
    clearHistory,
  };
};
